(ns ductile.ui.gate
  (:require [clojure.string :as str]
            [ductile.base64u :as base64u]
            [ductile.ui.components :as ui]
            [ductile.ui.components.table :as table]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.fahrzeug :as fahrzeug]
            [ductile.ui.http :as http]
            [ductile.ui.locale :as locale :refer [tr]]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.router :as router]
            [ductile.ui.scene :as scene]
            [ductile.ui.scope]
            [ductile.ui.transport :as transport]
            [ductile.ui.workflow :as workflow]
            [goog.string.format]
            [nextjournal.log :as log])
  (:require-macros [ductile.ui.macros :refer [defc]]
                   [ductile.ui.scope.macros :refer [when-current-user-scope]]))

;; visitors table

(defc visitors [visitors match]
  [:div
   [:div.mb-4.flex.items-center.justify-between
    [:h2.text-2xl.mx-2 (tr "Visitors")]
    [ui/button
     {:variant :primary
      :href (router/path '[compound :compound/name gate visitors new] (:path-params match))}
     [ui/icon :plus]
     (tr "Record Visit")]]
   [:div.w-full.overflow-auto.rounded.border.border-slate-300.shadow
    [table/table
     {:class :w-full
      :sticky-header? true
      :hide-columns [:ductile/id]
      :schema [:compound/name
               :visitor/name
               :visitor/visitor-number
               :company/name
               :visit/license-plate
               :ductile/note
               :entry/datetime
               :exit/datetime]
      :transform-columns {[:exit/datetime]
                          (fn [visitor]
                            (if-let [exit-datetime (:exit/datetime visitor)]
                              exit-datetime
                              [ui/button
                               {:on-click (fn [] (.then (http/$lambda
                                                         'ductile.gate-control/mark-visit-exited!
                                                         {:ductile/id (:ductile/id visitor)})
                                                        deframe/route-refetch!))}
                               (tr "Mark exited")]))}}
     visitors]]])

;; unified visits table

(defc carrier-card [carrier]
  [transport/location-text carrier])

(defc transporter-card [transporter]
  (carrier-card (:transport/carrier transporter))
  #_(let [address-name (get-in transporter [:transport/carrier :address/name])]
      [:div.px-3.py-2
       {:data-testid (str "truck-" (:truck/number transporter))}
       [:div
        [:div.text-sm (:truck/number transporter)]
        [:div.opacity-70.leading-tight {:class ["text-[11px]"]}
         [:div (tr :transport/license-plate) ": " (:transport/license-plate transporter)]
         [:div (tr :transport/carrier) ": " address-name]]]]))

;; what info do we want to see?
;; Truck number
;; License plate

(defc truck-number [truck-number]
  [:div.inline-flex.items-center
   [:div.flex.items-center.border.border-gray-700.gap-1.rounded.px-1.bg-white
    {:style {:height 22}}
    [:span.text-gray-700
     [ui/icon {:size 14} :truck]]
    [:span.text-xs (tr "Truck") " " truck-number]]])

(defc license-plate [license-plate]
  [:div.inline-flex.items-center
   [:div.flex.items-center.border.border-gray-700.gap-1.rounded.px-1.bg-white.relative.overflow-hidden
    {:style {:height 22 :padding-left 14}}
    [:div.absolute.left-0.top-0.flex.shrink-0.justify-center.items-center
     {:style {:height 22 :width 10}
      :class "bg-[#00349f]"}
     [:div.rounded-full.border.border-yellow-500.border-dotted {:class ["w-[5px]" "h-[5px]"]}]]
    [:span.text-xs.tabular-nums license-plate]]])

(defc visitor-name [visitor-name]
  [:div.inline-flex.items-center
   [:div.flex.items-center.border.border-gray-700.gap-1.rounded.px-1.bg-white
    {:style {:height 22}}
    [:span.text-gray-700
     [ui/icon {:size 14} :person]]
    [:span.text-xs visitor-name]]])

(defc visitor-card [visitor]
  [:div
   [visitor-name (:visitor/name visitor)]
   [:div (:company/name visitor)]
   [license-plate (:visit/license-plate visitor)]])

(defc delivery-card [delivery]
  [license-plate (:visit/license-plate delivery)])

(defc transport-card [visit]
  [:div.flex.flex-col.gap-1
   (if-let [transporter (:visit/transporter visit)]
     [truck-number (:truck/number transporter)]
     (when (:visit/license-plate visit)
       [license-plate (:visit/license-plate visit)]))
   (when (:visitor/name visit)
     [visitor-name (:visitor/name visit)])
   (cond (:visit/carrier visit)
         (carrier-card (:visit/carrier visit))
         (:visit/transporter visit)
         (transporter-card (:visit/transporter visit)))])

(defc visit-card [visit]
  (case (:visit/type visit)
    :visit.type/visitor [visitor-card visit]
    :visit.type/goods-delivery [delivery-card visit]
    :visit.type/transport [transport-card visit]))

(defc loading-line [load]
  [:div (tr :transport/loading-line) ": "
   (if-let [loading-line (:transport/loading-line load)]
     (str (:zone loading-line) (:row loading-line))
     (tr "Unassigned"))])

(defc entry-load-card [load]
  [:a {:href (router/path '[compound :compound/name transports :ductile/id] (assoc load :compound/name (-> load :destination/location :compound/name)))}
   [:div
    (:transport/name load)
    [loading-line load]
    (let [number-of-vehicles-on-load (:transport/assumed-vehicles-count load)]
      [:span.inline-flex.gap-1.items-center.tabular-nums
       [ui/icon {:size 12} :car]
       number-of-vehicles-on-load])]])

(defc exit-load-card [load]
  [:a {:href (router/path '[compound :compound/name transports :ductile/id] (assoc load :compound/name (-> load :origin/location :compound/name)))}
   [:div
    [:div (:transport/name load)]
    [:div (tr :load/id) ": " (:load/id load)]
    [loading-line load]
    [transport/vehicle-count-badge load]]])

(defn visit->wizard-data
  "Compute data to prefill the gate entry wizard with an existing visit to update it"
  [visit]
  (let [entry-load (first (:visit/entry-load visit))
        exit-load (first (:visit/exit-load visit))]
    (merge entry-load
           exit-load
           visit
           {:entry/transport? (some? entry-load)
            :exit/transport? (some? exit-load)}
           (when entry-load
             {:entry/transport [:ductile/id (:ductile/id entry-load)]})
           (when exit-load
             {:exit/transport [:ductile/id (:ductile/id exit-load)]}))))

(defc gate-book [data match]
  [:div
   [:div.mb-4.flex.items-center.justify-between
    [:h2.text-2xl.mx-2 (tr "Gate Book")]
    [:div.flex.items-center.gap-2
     (when-current-user-scope
      :scope/gatebook
      [ui/button
       {:variant :primary
        :on-click #(http/download "/api/lambda"
                                  {:lambda/handlers '[ductile.gate-control/gate-book-report
                                                      ductile.spreadsheet/export-xlsx]})}
       [ui/icon :excel]
       (tr "Export")])
     [ui/button
      {:variant :primary
       :href (router/path '[compound :compound/name gate deliveries new] (:path-params match))}
      [ui/icon :plus]
      (tr "Record Delivery")]
     [ui/button
      {:variant :primary
       :href (router/path '[compound :compound/name wizard gate] (:path-params match))}
      [ui/icon :plus]
      (tr "Record Transport")]]]
   [:div.w-full.overflow-auto.rounded.border.border-slate-300.shadow
    [table/table
     {:class :w-full
      :sticky-header? true
      :schema (cond->> [#_:compound/name
                        :visit/type
                        :visit/visit
                        #_:visit/license-plate
                        :visit/entry-load
                        :visit/exit-load
                        :ductile/note
                        :entry/expected-datetime
                        :arrival/datetime
                        :entry/datetime
                        :exit/datetime]
                (ui/debug-mode?) (concat [:ductile/id]))
      :transform-columns {[:ductile/id]
                          (fn [visit]
                            (str (:ductile/id visit)))
                          [:visit/type]
                          (fn [visit]
                            (when-let [visit-type (:visit/type visit)]
                              [:span.flex.items-center.gap-1
                               [ui/icon {:alt (tr visit-type) :size 16}
                                (case visit-type
                                  :visit.type/transport :truck
                                  :visit.type/goods-delivery :package
                                  :visit.type/visitor :person)]
                               (tr visit-type)]))
                          [:arrival/datetime]
                          (fn [visit]
                            (if-let [arrival-datetime (:arrival/datetime visit)]
                              arrival-datetime
                              (when-not (:arrival/datetime visit)
                                [ui/button
                                 {:href (router/path '[compound :compound/name wizard gate :wizard-state]
                                                     (assoc visit :wizard-state (-> visit
                                                                                    visit->wizard-data
                                                                                    base64u/encode)))}
                                 (tr "Mark as arrived")])))
                          [:entry/datetime]
                          (fn [visit]
                            (if-let [entry-datetime (:entry/datetime visit)]
                              entry-datetime
                              (when (:arrival/datetime visit)
                                [ui/button
                                 {:on-click (fn [] (.then (http/$lambda
                                                           'ductile.gate-control/mark-visit-entered!
                                                           {:ductile/id (:ductile/id visit)})
                                                          deframe/route-refetch!))}
                                 (tr "Mark as entered")])))
                          [:exit/datetime]
                          (fn [visit]
                            (if-let [exit-datetime (:exit/datetime visit)]
                              exit-datetime
                              (when (:entry/datetime visit)
                                [ui/button
                                 {:on-click (fn [] (.then (http/$lambda
                                                           'ductile.gate-control/mark-visit-exited!
                                                           {:ductile/id (:ductile/id visit)})
                                                          deframe/route-refetch!))}
                                 (tr "Mark exited")])))
                          [:visit/visit]
                          (fn [visit]
                            (visit-card visit)
                            #_[combobox/transporter-or-carrier-choice (or (:visit/transporter visit)
                                                                          (:visit/carrier visit))])
                          [:visit/entry-load]
                          (fn [visit]
                            (->> visit
                                 :visit/entry-load
                                 (into [:div] (map entry-load-card))))
                          [:visit/exit-load]
                          (fn [visit]
                            (if-let [exit-load (->> visit
                                                    :visit/exit-load
                                                    first)]
                              [:div.flex.gap-2.justify-between
                               (exit-load-card exit-load)
                               [ui/button
                                {:on-click (fn [] (ui/show-form-modal! 'ductile.gate-control/show-unlink-exit-load-form {:ductile/id (:ductile/id visit)}))
                                 :disabled? (or (:exit/datetime exit-load) (:exit/datetime visit))}
                                [ui/icon :cross-circled]
                                [:span (tr "Unlink")]]]
                              (when (and (= :visit.type/transport (:visit/type visit)) (not (:exit/datetime visit)))
                                [ui/button
                                 {:on-click (fn [] (ui/show-form-modal! 'ductile.gate-control/show-add-exit-load-form {:ductile/id (:ductile/id visit)}))}
                                 (tr "Add exit load")])))}}
     data]]])

(defc generate-self-checkin-link [fetch match]
  [:div
   [mobile/table
    {:title (tr :transport/carrier)
     :items [[workflow/carrier-ref-view
              @(deframe/!route-get :transport/carrier)
              [:transport/carrier]
              {:ui/options fetch
               :ui/label-key :address/name}]]}]
   (let [link (str
               (.-origin js/location)
               (router/path '[compound :compound/name gate self-checkin]
                            (:path-params match)
                            (str "b64=" (base64u/encode {:transport/carrier @(deframe/!route-get :transport/carrier)}))))]
     [mobile/table
      {:title (tr "Link")
       :items [[ui/link {:href link} link]
               [ui/button {:variant :white
                           :on-click #(.writeText js/navigator.clipboard link)}
                [ui/icon :copy]
                (tr "copy")]]}])])

(defc show-checkin [checkin-fetch _match]
  [:div
   [:p (tr "Thank you for your registration.")]
   [:p (tr "Please show up during the opening hours of the compound.")]
   #_[ui/qr-code (:ductile/id checkin-fetch)]
   [fahrzeug/entity-view
    {:exclude [:ductile/id]}
    checkin-fetch]])
